// Base of ExperienceData: builds the lookup tables (articles, clusters,
// integrations, merchants by id) from the payload, warns on dangling ids, and
// answers cluster, timeline and article lookups.
import { must } from "@/common/utils/must";
import Integration from "@/modules/Integration";
import { clusterTypes } from "@/modules/experienceConstants";
import type {
  ExperienceArticle,
  ExperienceCluster,
  ExperienceId,
  ExperiencePayload,
  TimelineEntry,
} from "@/types/experience";
import type { MerchantPayloadEntry } from "@/modules/ExperienceData";

export default class ExperienceClusters {
  declare idintegration: ExperiencePayload["idintegration"];
  declare articles: ExperiencePayload["articles"];
  declare clusters: ExperiencePayload["clusters"];
  declare integrations: ExperiencePayload["integrations"];
  declare merchants: ExperiencePayload["merchants"];
  declare articlesById: Record<string, ExperienceArticle>;
  declare integrationsById: Record<string, Integration>;
  declare clusterMap: Record<string, ExperienceCluster[]>;
  declare timelineName: ExperiencePayload["timelineName"];

  // Check to ensure cluster article ids are present in the articles list.
  _checkClusters() {
    if (this.clusters) {
      for (let k = 0, maxk = this.clusters.length; k < maxk; k++) {
        const cluster = this.clusters[k];
        const ids = cluster.ids;
        if (ids) {
          for (let n = 0, maxn = ids.length; n < maxn; n++) {
            if (!(ids[n] in this.articlesById)) {
              console.warn(
                `ERROR: Cannot find cluster article in article list: ${ids[n]}`,
              );
            }
          }
        }
        const timeline = cluster.timeline;
        if (timeline) {
          for (let n = 0, maxn = timeline.length; n < maxn; n++) {
            const timelineData = timeline[n];
            for (
              let m = 0, maxm = timelineData.ids ? timelineData.ids.length : 0;
              m < maxm;
              m++
            ) {
              if (!(timelineData.ids[m] in this.articlesById)) {
                console.warn(
                  "ERROR: Cannot find timeline article in article list: " +
                    timelineData.ids[k],
                );
              }
            }
          }
        }
      }
    }
  }

  _checkIntegrations() {
    if (
      this.idintegration &&
      this.integrationsById[this.idintegration] === undefined
    ) {
      console.warn(`Ingration not foind for id ${this.idintegration}`);
    }
  }

  _createClusterMap(): Record<string, ExperienceCluster[]> {
    const ret: Record<string, ExperienceCluster[]> = {};
    if (this.clusters) {
      for (let k = 0, maxk = this.clusters.length; k < maxk; k++) {
        const cluster = this.clusters[k];
        let clusterList = ret[cluster.name];
        if (clusterList == null) {
          clusterList = [];
          ret[cluster.name] = clusterList;
        }

        clusterList.push(cluster);
      }

      // sort the clusters by order
      const keys = Object.keys(ret);
      for (let j = 0, maxj = keys.length; j < maxj; j++) {
        const clusters = ret[keys[j]];
        if (clusters.length > 1) {
          clusters.sort((a, b) => {
            if (a.order < b.order) {
              return -1;
            }
            if (b.order < a.order) {
              return 1;
            }
            return 0;
          });
        }
      }
    }

    // Tag-based visibility only for cookware, book, product (Essentials). Wine and others use cluster-only.
    const tagBackedCategoryNames = ["product", "book", "cookware"];
    for (let t = 0, maxt = tagBackedCategoryNames.length; t < maxt; t++) {
      const tagName = tagBackedCategoryNames[t];
      const tagIds = this._getArticleIdsByCategoryTag(tagName);
      if (tagName in ret) {
        const clusterData = ret[tagName][0];
        const emptyExperience =
          clusterData &&
          !clusterData.timeline &&
          (!clusterData.ids || clusterData.ids.length === 0);
        if (emptyExperience && (!tagIds || tagIds.length === 0)) {
          delete ret[tagName];
          continue;
        }
        if (emptyExperience && tagIds && tagIds.length > 0) {
          clusterData.ids = tagIds;
        }
      } else if (tagIds && tagIds.length > 0) {
        ret[tagName] = [
          { name: tagName, order: 0, ids: tagIds, timeline: null },
        ];
      }
    }
    // ProductPlugin uses cluster "root"; only show when cluster has ids or articles tagged "product"
    const rootTagIds = this._getArticleIdsByCategoryTag("product");
    if ("root" in ret) {
      const rootData = ret["root"][0];
      const rootEmpty =
        rootData &&
        !rootData.timeline &&
        (!rootData.ids || rootData.ids.length === 0);
      if (rootEmpty && (!rootTagIds || rootTagIds.length === 0)) {
        delete ret["root"];
      } else if (rootEmpty && rootTagIds && rootTagIds.length > 0) {
        rootData.ids = rootTagIds;
      }
    } else if (rootTagIds && rootTagIds.length > 0) {
      ret["root"] = [
        { name: "root", order: 0, ids: rootTagIds, timeline: null },
      ];
    }

    return ret;
  }

  /**
   * Returns article ids that have a tag with name === categoryName and type === 'article_category'.
   */
  _getArticleIdsByCategoryTag(categoryName: string): ExperienceId[] {
    const ids: ExperienceId[] = [];
    if (!this.articles) {
      return ids;
    }
    for (let i = 0, maxi = this.articles.length; i < maxi; i++) {
      const article = this.articles[i];
      const tags = article.tags;
      if (!tags || !tags.length) {
        continue;
      }
      for (let j = 0, maxj = tags.length; j < maxj; j++) {
        if (
          tags[j].type === "article_category" &&
          tags[j].name === categoryName
        ) {
          ids.push(article.id);
          break;
        }
      }
    }
    return ids;
  }

  /**
   * True if any article has a tag with name === categoryName and type === 'article_category'.
   */
  _createArticlesById(): Record<string, ExperienceArticle> {
    const ret: Record<string, ExperienceArticle> = {};
    if (this.articles) {
      for (let i = 0, maxi = this.articles.length; i < maxi; i++) {
        const article = this.articles[i];
        ret[article.id.toString()] = article;
      }
    }
    return ret;
  }

  _createIntegrationsById(): Record<string, Integration> {
    const ret: Record<string, Integration> = {};
    if (this.integrations !== undefined) {
      for (let l = 0, maxl = this.integrations.length; l < maxl; l++) {
        const integration = this.integrations[l];
        ret[integration.id] = new Integration(integration);
      }
    }
    return ret;
  }

  _createMerchantsById(): Record<string, MerchantPayloadEntry> {
    const ret: Record<string, MerchantPayloadEntry> = {};
    if (this.merchants !== undefined) {
      for (let l = 0, maxl = this.merchants.length; l < maxl; l++) {
        const merchant = this.merchants[l];
        ret[merchant.idmerchant] = merchant;
      }
    }
    return ret;
  }

  getClusterType(name: string | undefined): string {
    const clusterData = this.getClusterData(name, 0);
    if (clusterData) {
      if (clusterData.timeline) {
        return clusterTypes.TIMELINE;
      } else if (clusterData.ids) {
        return clusterTypes.EXPERIENCE;
      }
    }
    return clusterTypes.NONE;
  }

  getTimelineData(
    name: string | undefined,
    cindex?: number,
  ): ExperienceCluster | null {
    return this.getClusterType(name) === clusterTypes.TIMELINE
      ? this.getClusterData(name, cindex) || null
      : null;
  }

  getTimeline(
    name: string | undefined,
    cindex?: number,
  ): TimelineEntry[] | null | undefined {
    const timelineData = this.getTimelineData(name, cindex);
    return timelineData ? timelineData.timeline : null;
  }

  getTimelineItem(
    name: string | undefined,
    cindex: number | undefined,
    index: number,
  ): TimelineEntry | null {
    const timelineData = this.getTimelineData(name, cindex);
    if (timelineData === null || index < 0) {
      return null;
    }
    const timeline = must(timelineData.timeline, "cluster timeline");
    return index >= timeline.length ? null : timeline[index];
  }

  getTimelineArticle(
    name: string | undefined,
    cindex: number | undefined,
    index: number,
  ): ExperienceArticle | null {
    const timelineItem = this.getTimelineItem(name, cindex, index);
    if (timelineItem === null) {
      return null;
    }
    return this.getArticleById(timelineItem.ids[0]);
  }

  getExperienceClusterData(
    name: string | undefined,
    cindex?: number,
  ): ExperienceCluster | null {
    if (name === undefined) {
      name = this.timelineName;
    }
    return this.getClusterType(name) === clusterTypes.EXPERIENCE
      ? this.getClusterData(name, cindex) || null
      : null;
  }

  getExperienceArticle(
    name: string | undefined,
    cindex: number | undefined,
    index: number,
  ): ExperienceArticle | null {
    const clusterData = this.getExperienceClusterData(name, cindex);
    if (clusterData === null) {
      return null;
    }
    const id = clusterData.ids[index];
    return id ? this.getArticleById(id) || null : null;
  }

  getClusterList(name: string | undefined): ExperienceCluster[] | null {
    return this.clusterMap[String(name)] || null;
  }

  getClusterData(
    name: string | undefined,
    cindex?: number,
  ): ExperienceCluster | null {
    if (cindex === undefined) {
      cindex = 0;
    }
    const clusterList = this.clusterMap[String(name)];
    return clusterList ? clusterList[cindex] || null : null;
  }

  getClusterCount(name: string | undefined): number {
    const clusterList = this.clusterMap[String(name)];
    if (clusterList) {
      return clusterList.length;
    }
    return 0;
  }

  getTotalClusterSize(name: string | undefined): number {
    const clusterList = this.clusterMap[String(name)];
    let ret = 0;
    if (clusterList) {
      for (let n = 0, max = clusterList.length; n < max; n++) {
        const cluster = clusterList[n];
        if (cluster.timeline) {
          ret += cluster.timeline.length;
        } else if (cluster.ids) {
          ret += cluster.ids.length;
        }
      }
    }
    return ret;
  }

  getClusterSize(name: string | undefined, cindex?: number): number {
    const clusterData = this.getClusterData(name, cindex);
    let ret = 0;
    if (clusterData) {
      if (clusterData.timeline) {
        ret = clusterData.timeline.length;
      } else if (clusterData.ids) {
        ret = clusterData.ids.length;
      }
    }
    return ret;
  }

  getArticle(
    name: string | undefined,
    cindex: number | undefined,
    index: number,
  ): ExperienceArticle | null {
    return (
      this.getTimelineArticle(name, cindex, index) ||
      this.getExperienceArticle(name, cindex, index)
    );
  }

  getArticleById(id: ExperienceId): ExperienceArticle | null {
    const key = id.toString();
    return this.articlesById[key] || null;
  }

  getArticleId(
    name: string | undefined,
    cindex: number | undefined,
    index: number,
  ): ExperienceId | undefined {
    const timelineItem = this.getTimelineItem(name, cindex, index);
    if (timelineItem !== null) {
      return timelineItem.ids ? timelineItem.ids[0] : -1;
    } else {
      const clusterData = this.getExperienceClusterData(name, cindex);
      if (clusterData) {
        return clusterData.ids[index] || -1;
      }
    }
  }

  getArticleName(
    name: string | undefined,
    cindex: number | undefined,
    index: number,
  ): string | null {
    const article = this.getArticle(name, cindex, index);
    let ret;

    if (article === null) {
      ret = null;
    } else {
      ret = article.name || null;
    }

    if (ret && ret.length > 30) {
      ret = `${ret.substring(0, 30)}\u2026`;
    }
    return ret;
  }

  getArticleAnnotation(
    name: string | undefined,
    cindex: number | undefined,
    index: number,
  ): string | null {
    const article = this.getArticle(name, cindex, index);
    let annotation;

    if (article === null) {
      annotation = null;
    } else {
      annotation = article.annotation || null;
    }

    if (annotation && annotation.length > 35) {
      annotation = `${annotation.substring(0, 35)}\u2026`;
    }
    return annotation;
  }
}
